import * as React from 'react';
import Header from '../search-page/components/header/Header';
import Footer from '../search-page/components/footer/Footer';
import { useEnhancedReducer } from '../../includes/enhanced-reducer';
import useApiQueryDataUpdate from '../search-page/hooks/useApiQueryDataUpdate';
import { VIEW_MODE_OPTIONS } from '../../../catalog-page/constants';
import { createCatalogReducer } from '../../../catalog-page/reducer';
import { initSearchPageData } from '../search-page/actions/searchPage';
import { IApiQueryData, ISearchPageActionState, ISearchResponse } from '../search-page/interfaces';
import {
    CATALOG_PAGE_CONTEXT_NAME,
    FILTER_ITEM_SELECT_DEBOUNCE_TIME_MS,
} from '../search-page/constants';
import { numberFormat } from '../../includes/number';
import { IBikeItem } from '../../../bike/interfaces';
import BikeGrid from 'PyzShopUi/scripts/bike-search-page/components/search-page-content/bike-list/BikeGrid';
import FilterStateService from '../search-page/services/FilterStateService';
import { DEFAULT_CURRENCY_FACTOR } from 'PyzShopUi/scripts/shop-ui/constants';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';

interface IBikeListProps {
    apiUrl: string;
    defaultParameter: IApiQueryData;
    filterStateService: FilterStateService;
    isCompactMode: boolean;
    searchResponse?: ISearchResponse;
    csrfToken: string;
}

const emptyBikeListPageState: ISearchPageActionState = {
    sort: {
        sortParamNames: [],
        currentSortParam: '',
    },
    pagination: {
        numFound: 0,
        currentPage: 1,
        maxPage: 1,
        currentItemsPerPage: 0,
        config: {
            defaultItemsPerPage: 48,
            validItemsPerPageOptions: [],
        },
    },
    searchResultItems: [],
    filters: [],
    storeConfig: {
        currency: '',
        currencyFactor: DEFAULT_CURRENCY_FACTOR,
        priceMode: 'GROSS_MODE',
    },
    extraInformation: {
        breadcrumb: null,
    },
    isPageLoading: false,
    apiQueryData: {},
    updatePageDebounceTime: FILTER_ITEM_SELECT_DEBOUNCE_TIME_MS,
    skipUpdateBaseOnApiQueryParams: false,
    initCategoryKey: null,
    activeCategoryName: '',
    headTitle: ''
};

const BikeListContainer: React.FunctionComponent<IBikeListProps> = props => {
    const {
        filterStateService,
        apiUrl,
        defaultParameter,
        isCompactMode,
        searchResponse
    } = props;

    const [state, dispatch] = useEnhancedReducer(createCatalogReducer(props.filterStateService), emptyBikeListPageState);

    const {
        pagination,
        sort,
        isPageLoading,
        apiQueryData,
        updatePageDebounceTime,
        skipUpdateBaseOnApiQueryParams,
        viewMode
    } = state;

    const hasViewMode = false;
    const { translate } = useTranslationHook();

    const itemPerPageOptions: number[] = pagination?.config?.validItemsPerPageOptions || [];
    const currentItemsPerPage: string = pagination?.currentItemsPerPage?.toString() || '';
    const pageNumFound: number = pagination?.numFound || 0;
    const currentPage: number = pagination?.currentPage || 0;
    const maxPage: number = pagination?.maxPage || 0;
    const totalSearchResultItemsText: string = translate('bike', { ns: 'bike-db', count: pageNumFound });

    useApiQueryDataUpdate(
        apiQueryData,
        dispatch,
        updatePageDebounceTime,
        filterStateService,
        skipUpdateBaseOnApiQueryParams,
        [],
        CATALOG_PAGE_CONTEXT_NAME
    );

    React.useEffect(() => {
        if (searchResponse !== null) {
            dispatch(initSearchPageData(apiUrl, defaultParameter, filterStateService, [], null, CATALOG_PAGE_CONTEXT_NAME, searchResponse));

            return;
        }

        dispatch(initSearchPageData(apiUrl, defaultParameter, filterStateService, [], null, CATALOG_PAGE_CONTEXT_NAME));
    }, []);

    const displayResultHeader = () => {
        if (isPageLoading || pageNumFound === 0 || isCompactMode) {
            return null;
        }

        const shouldRenderSort = sort.sortParamNames.length > 1;

        return (
            <Header
                itemPerPageOptions={itemPerPageOptions}
                currentItemsPerPage={currentItemsPerPage}
                totalSearchResultItemsText={totalSearchResultItemsText}
                sortParamNames={sort.sortParamNames}
                currentSortParam={sort.currentSortParam}
                renderSortSelection={shouldRenderSort}
                renderPerPageSelection={true}
                renderViewModeSwitch={hasViewMode}
                availableViewModes={VIEW_MODE_OPTIONS}
                viewMode={viewMode}
                dispatch={dispatch}
            />
        );
    };

    const displayResultFooter = () => {
        if (isPageLoading || pageNumFound === 0 || isCompactMode) {
            return null;
        }

        return (
            <Footer
                perPageSelectText={translate('Bikes per page', { ns: 'search'})}
                itemPerPageOptions={itemPerPageOptions}
                currentItemsPerPage={currentItemsPerPage}
                currentPage={currentPage}
                renderPerPageSelection={true}
                maxPage={maxPage}
                dispatch={dispatch}
            />
        );
    };

    const displayBikeList = () => {
        const { searchResultItems } = state;

        return (
            <BikeGrid bikes={searchResultItems as IBikeItem[]} hasSideBar={false} csrfToken={props.csrfToken}/>
        );
    };

    return (
        <React.Fragment>
            <div className='bike-tab'>
                <div className='grid-x grid-padding-x'>
                    <div className='cell small-12 medium-12 large-12 page-layout-search-items-result'>
                        <div className='result-header grid-padding-y'>
                            {displayResultHeader()}
                        </div>

                        <div className='grid-x grid-padding-x bikes'>
                            {displayBikeList()}
                        </div>

                        <div className='result-footer'>
                            {displayResultFooter()}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default BikeListContainer;
